import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  Globe,
  MessagesSquare,
  Shield,
  Bell,
  CreditCard
} from 'lucide-react';

interface AdminSidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
}

const AdminSidebar = ({ open, setOpen, isMobile }: AdminSidebarProps) => {
  const location = useLocation();
  const { t } = useTranslation();

  const navItems = [
    {
      title: t('admin.sidebar.dashboard', 'Dashboard'),
      href: '/admin',
      icon: LayoutDashboard,
      exact: true
    },
    {
      title: t('admin.sidebar.chatLogs', 'Chat Logs'),
      href: '/admin/chat-logs',
      icon: MessageSquare
    },
    {
      title: t('admin.sidebar.documents', 'Documents'),
      href: '/admin/documents',
      icon: FileText
    },
    {
      title: t('admin.sidebar.siteSync', 'Site Sync'),
      href: '/admin/site-sync',
      icon: Globe
    },
    {
      title: t('admin.sidebar.comments', 'Comments'),
      href: '/admin/comments',
      icon: MessagesSquare
    },
    {
      title: t('admin.sidebar.admins', 'Admins'),
      href: '/admin/admins',
      icon: Shield
    },
    {
      title: t('admin.sidebar.messages', 'Messages'),
      href: '/admin/messages',
      icon: Bell,
      aliases: ['/admin/telegram-messages', '/admin/whatsapp-messages']
    },
    {
      title: t('admin.sidebar.cards', 'Cards'),
      href: '/admin/cards',
      icon: CreditCard
    }
  ];

  const isActive = (href: string, exact?: boolean, aliases?: string[]) => {
    if (exact) {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href) || Boolean(aliases?.some((alias) => location.pathname.startsWith(alias)));
  };

  const navContent = (
    <>
      <div className="flex h-16 shrink-0 items-center border-b border-border px-6">
        <h2 className="text-xl font-bold">{t('admin.title', 'Admin Panel')}</h2>
      </div>
      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href, item.exact, item.aliases);

            return (
              <li key={item.href}>
                <Link
                  to={item.href}
                  onClick={() => { if (isMobile) setOpen(false); }}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200",
                    active
                      ? "bg-primary text-primary-foreground font-medium shadow-sm"
                      : "fongpt-hover text-secondary"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.title}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-64 flex flex-col">
          <SheetTitle className="sr-only">{t('admin.title', 'Admin Panel')}</SheetTitle>
          {navContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className={cn(
        "bg-card border-r border-border h-screen sticky top-0 flex flex-col transition-all duration-300",
        open ? "w-64" : "w-0 overflow-hidden"
      )}
    >
      {navContent}
    </div>
  );
};

export default AdminSidebar;
